import { useState, useEffect, useCallback, useRef } from 'react';

const COLLAPSED_STORAGE_KEY = 'backstage.sidebar.collapsed';
const EXPANDED_STORAGE_KEY = 'backstage.sidebar.expanded';

export const useSidebarState = () => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    return stored === 'true';
  });

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () => {
      if (typeof window === 'undefined') return {};
      try {
        const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
      } catch {
        return {};
      }
    },
  );

  useEffect(() => {
    localStorage.setItem(COLLAPSED_STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const handleGroupToggle = useCallback(
    (groupId: string, expanded: boolean) => {
      setExpandedGroups(prev => ({ ...prev, [groupId]: expanded }));
    },
    [],
  );

  // Hover state for collapsed mode popouts - coordinates across all groups
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus state for keyboard navigation - coordinates across all groups
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);

  // Active submenu state - tracks which submenu has keyboard focus trap active
  const [activeSubmenuGroupId, setActiveSubmenuGroupId] = useState<
    string | null
  >(null);

  // Function to reset keyboard navigation state
  const resetKeyboardNav = useCallback(() => {
    if (activeSubmenuGroupId !== null) {
      setActiveSubmenuGroupId(null);
    }
    if (focusedGroupId !== null) {
      setFocusedGroupId(null);
    }
  }, [activeSubmenuGroupId, focusedGroupId]);

  const handleGroupHover = useCallback(
    (groupId: string | null) => {
      // Track if there was a pending timeout before clearing it
      const hadPendingTimeout = hoverTimeoutRef.current !== null;

      // Always clear any pending timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }

      if (groupId !== null) {
        // Reset keyboard navigation state when hovering (switching from keyboard to mouse)
        // This ensures that any hover resets the keyboard nav state
        resetKeyboardNav();

        if (groupId === hoveredGroupId) {
          // Same group - no change needed (clears any pending close)
          return;
        }

        if (hoveredGroupId === null) {
          // No menu currently open - open immediately
          setHoveredGroupId(groupId);
        } else if (hadPendingTimeout) {
          // If there was a pending close timeout, immediately switch to new group
          // This handles the case where user hovers new group while close timeout is pending
          setHoveredGroupId(groupId);
        } else {
          // Switching between groups - short delay for hover intent
          // This prevents accidental switches when moving to submenu
          hoverTimeoutRef.current = setTimeout(() => {
            setHoveredGroupId(groupId);
          }, 30);
        }
      } else {
        // Delay hiding to allow moving from main item to popout
        hoverTimeoutRef.current = setTimeout(() => {
          setHoveredGroupId(null);
        }, 100);
      }
    },
    [hoveredGroupId, resetKeyboardNav],
  );

  const handleGroupFocus = useCallback((groupId: string | null) => {
    setFocusedGroupId(groupId);
  }, []);

  const handleSubmenuActivate = useCallback((groupId: string | null) => {
    setActiveSubmenuGroupId(groupId);
    // When activating a submenu, also set focus to that group
    if (groupId !== null) {
      setFocusedGroupId(groupId);
    } else {
      // When deactivating, clear focus as well
      setFocusedGroupId(null);
    }
  }, []);

  return {
    isCollapsed,
    expandedGroups,
    onToggleCollapse: handleToggleCollapse,
    onGroupToggle: handleGroupToggle,
    hoveredGroupId,
    onGroupHover: handleGroupHover,
    focusedGroupId,
    onGroupFocus: handleGroupFocus,
    activeSubmenuGroupId,
    onSubmenuActivate: handleSubmenuActivate,
    resetKeyboardNav,
  };
};
